import { Button, Card, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type {
	SubscriberType,
	ValidationAndOtherDetailParameters,
	ValidationAndOtherDetailRateType,
	ValidationAndOtherDetailReportFormat,
} from '@parkchirp/reporting';
import { validationAndOtherDetailDefaultRateTypes } from '@parkchirp/reporting';
import { useCallback, useMemo, useState } from 'react';
import { dayjs } from '../../utils/dayjs';
import { AccountFilter, type AccountFilterValue } from './AccountFilter';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { InvoiceDateFilter, type InvoiceDateFilterValue } from './InvoiceDateFilter';
import { SubscriberTypeFilter } from './SubscriberTypeFilter';
import type { ReportFormProps } from './types';

const initialDateFilter: InvoiceDateFilterValue = {
	invoiceStartDate: null,
	invoiceEndDate: null,
	parkingPeriodStartMonth: '',
	parkingPeriodStartYear: '',
	parkingPeriodEndMonth: '',
	parkingPeriodEndYear: '',
};

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

const hasParkingPeriodStart = (dateFilter: InvoiceDateFilterValue) => {
	return dateFilter.parkingPeriodStartMonth !== '' && dateFilter.parkingPeriodStartYear !== '';
};

export const ValidationAndOtherDetailReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateFilter, setDateFilter] = useState<InvoiceDateFilterValue>(initialDateFilter);
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: 'all' });
	const [rateTypes, setRateTypes] = useState<ValidationAndOtherDetailRateType[]>([
		...validationAndOtherDetailDefaultRateTypes,
	]);
	const [subscriberType, setSubscriberType] = useState<SubscriberType>('All');
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [format, setFormat] = useState<ValidationAndOtherDetailReportFormat>('csv');

	const canSubmit = useMemo(() => {
		if (rateTypes.length === 0) {
			return false;
		}

		return Boolean(dateFilter.invoiceStartDate) || hasParkingPeriodStart(dateFilter);
	}, [dateFilter, rateTypes]);

	const reset = useCallback(() => {
		setDateFilter(initialDateFilter);
		setAccountFilter({ type: 'all' });
		setRateTypes([...validationAndOtherDetailDefaultRateTypes]);
		setSubscriberType('All');
		setFacilityHierarchy(initialFacilityHierarchy);
		setFormat('csv');
	}, []);

	const allRateTypesSelected = rateTypes.length === validationAndOtherDetailDefaultRateTypes.length;

	const handleRateTypeChange = useCallback((rateType: ValidationAndOtherDetailRateType, checked: boolean) => {
		if (checked) {
			setRateTypes((current) => [...current, rateType]);
			return;
		}

		setRateTypes((current) => current.filter((selected) => selected !== rateType));
	}, []);

	const buildAccountFilter = (): ValidationAndOtherDetailParameters['accountFilter'] => {
		switch (accountFilter.type) {
			case 'accountId':
				return {
					type: 'accountId',
					value: Number(accountFilter.value),
				};

			case 'subscriptionName':
				return {
					type: 'subscriptionName',
					value: accountFilter.value.trim(),
				};
			default:
				return undefined;
		}
	};

	const buildParameters = (): ValidationAndOtherDetailParameters => {
		const parameters: ValidationAndOtherDetailParameters = {
			rateTypes,
			subscriberType,
			format,
		};

		if (dateFilter.invoiceStartDate) {
			parameters.invoiceStartDate = dateFilter.invoiceStartDate.format('YYYY-MM-DD');

			if (dateFilter.invoiceEndDate) {
				parameters.invoiceEndDate = dateFilter.invoiceEndDate.format('YYYY-MM-DD');
			}
		}

		if (hasParkingPeriodStart(dateFilter)) {
			parameters.parkingPeriodStartDate = dayjs()
				.year(dateFilter.parkingPeriodStartYear as number)
				.month(dateFilter.parkingPeriodStartMonth as number)
				.startOf('month')
				.format('YYYY-MM-DD');

			if (dateFilter.parkingPeriodEndMonth !== '' && dateFilter.parkingPeriodEndYear !== '') {
				parameters.parkingPeriodEndDate = dayjs()
					.year(dateFilter.parkingPeriodEndYear)
					.month(dateFilter.parkingPeriodEndMonth)
					.endOf('month')
					.format('YYYY-MM-DD');
			}
		}

		const account = buildAccountFilter();
		if (account) {
			parameters.accountFilter = account;
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		if (facilityHierarchy.facilities.length === 1) {
			const facilityNumber = facilityHierarchy.facilities[0].facilityNumber;
			if (facilityNumber !== undefined && facilityNumber !== null && `${facilityNumber}` !== '') {
				parameters.facilityNumber = facilityNumber;
			}
		}

		return parameters;
	};

	const validate = () => {
		if (rateTypes.length === 0) {
			onValidationError('Select at least one rate type.');
			return false;
		}

		if (!dateFilter.invoiceStartDate && !hasParkingPeriodStart(dateFilter)) {
			onValidationError('Select an invoice start date or a parking period start month and year.');
			return false;
		}

		if (
			(dateFilter.parkingPeriodEndMonth !== '' || dateFilter.parkingPeriodEndYear !== '') &&
			!hasParkingPeriodStart(dateFilter)
		) {
			onValidationError('Select a parking period start before specifying a parking period end.');
			return false;
		}

		if (accountFilter.type === 'accountId' && !accountFilter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (accountFilter.type === 'subscriptionName' && !accountFilter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'validationAndOtherDetail',
			parameters: buildParameters(),
		});
	};

	return (
		<Card variant='outlined' sx={{ p: 3 }}>
			<Stack spacing={3}>
				<InvoiceDateFilter value={dateFilter} onChange={setDateFilter} />

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<SubscriberTypeFilter value={subscriberType} onChange={setSubscriberType} />

				<Divider />

				<Stack spacing={2}>
					<Stack direction='row' justifyContent='space-between' alignItems='center'>
						<Typography variant='subtitle1' fontWeight={600}>
							Rate types
						</Typography>

						<Stack direction='row' spacing={1}>
							<Button
								size='small'
								variant='text'
								onClick={() => setRateTypes([...validationAndOtherDetailDefaultRateTypes])}
								disabled={allRateTypesSelected}
							>
								Select all
							</Button>
							<Button
								size='small'
								variant='text'
								onClick={() => setRateTypes([])}
								disabled={rateTypes.length === 0}
							>
								Clear
							</Button>
						</Stack>
					</Stack>

					<Stack direction='row' flexWrap='wrap' columnGap={3} rowGap={1}>
						{validationAndOtherDetailDefaultRateTypes.map((rateType) => (
							<FormControlLabel
								key={rateType}
								control={
									<Checkbox
										checked={rateTypes.includes(rateType)}
										onChange={(event) => handleRateTypeChange(rateType, event.target.checked)}
									/>
								}
								label={rateType}
							/>
						))}
					</Stack>
				</Stack>

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: ValidationAndOtherDetailReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						<ToggleButton value='pdf'>PDF</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack direction='row' justifyContent='flex-end' spacing={2}>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
